import { getJson } from '@/lib/api';
import {
  NO_VALUE,
  PROGRAMS_DEFAULT_FROM,
  type ProgramMetrics,
  type QuarterMargin,
  type ReportWindow,
} from '@/lib/program-profitability';

/**
 * Profit per rep on the sales dashboard: typed fetchers over
 * /api/v3/sales/profitability. Shapes mirror apps/api's rep-report. The API
 * serves these only to staff holding view_sales_dashboard AND
 * view_accounting; the pages check the same pair before asking.
 */

export interface RepOwner {
  readonly ownerId: string;
  readonly ownerName: string;
  readonly onRoster: boolean;
}

export interface RepComparisons {
  readonly expectedMargin: number | null;
  /** Actual − expected margin, as a fraction (0.032 = +3.2 pts). */
  readonly marginVsMix: number | null;
  /** 100 = the company median price per participant. */
  readonly priceIndex: number | null;
  readonly pricedEvents: number;
}

export interface RepProfitRow extends RepOwner, ProgramMetrics, RepComparisons {}

export interface RepProfitabilityReport {
  readonly window: ReportWindow;
  readonly targetMargin: number;
  readonly minProgramEvents: number;
  readonly priceFlagBelow: number;
  readonly company: ProgramMetrics;
  readonly reps: readonly RepProfitRow[];
}

export interface RepProgramRow {
  readonly program: string;
  readonly events: number;
  readonly revenue: number;
  readonly contribution: number;
  readonly margin: number | null;
  readonly mixMargin: number | null;
  readonly usesCompanyMargin: boolean;
  readonly participants: number;
  readonly revenuePerParticipant: number | null;
  readonly companyMedianRevenuePerParticipant: number | null;
  readonly priceIndex: number | null;
  readonly priceComparable: boolean;
  readonly flagged: boolean;
}

export interface RepQuarter extends QuarterMargin {
  readonly companyMargin: number | null;
}

export interface RepProfitabilityDetail {
  readonly window: ReportWindow;
  readonly targetMargin: number;
  readonly minProgramEvents: number;
  readonly priceFlagBelow: number;
  readonly owner: RepOwner;
  readonly metrics: ProgramMetrics;
  readonly comparisons: RepComparisons;
  readonly company: ProgramMetrics;
  readonly quarters: readonly RepQuarter[];
  readonly programs: readonly RepProgramRow[];
}

/** The window: event dates, `to` exclusive. */
export interface ProfitWindowQuery {
  readonly from: string;
  readonly to: string;
}

/**
 * URL keys for the profit section. Prefixed so they never collide with the
 * reps table and the opportunity table already on these pages.
 */
export const PROFIT_PARAM = {
  from: 'profit_from',
  to: 'profit_to',
} as const;

/** The table prefix for useClientTable (`?profit_q=&profit_sort=`…). */
export const PROFIT_TABLE_PREFIX = 'profit_';

export const PROFIT_DEFAULT_FROM = PROGRAMS_DEFAULT_FROM;

export const SALES_PROFIT_QUERY_KEY = {
  report: (query: ProfitWindowQuery) => ['sales', 'profitability', query] as const,
  rep: (ownerId: string, query: ProfitWindowQuery) => ['sales', 'profitability', ownerId, query] as const,
} as const;

export function readRepProfitability(query: ProfitWindowQuery, signal?: AbortSignal): Promise<RepProfitabilityReport> {
  return getJson<RepProfitabilityReport>('/sales/profitability', { from: query.from, to: query.to }, signal);
}

export function readRepProfitabilityDetail(
  ownerId: string,
  query: ProfitWindowQuery,
  signal?: AbortSignal,
): Promise<RepProfitabilityDetail> {
  return getJson<RepProfitabilityDetail>(
    `/sales/reps/${encodeURIComponent(ownerId)}/profitability`,
    { from: query.from, to: query.to },
    signal,
  );
}

// ─── Display ─────────────────────────────────────────────────────────────

const POINTS_PER_UNIT = 100;

/** 0.032 → "+3.2 pts", -0.021 → "-2.1 pts". */
export function pointsLabel(difference: number | null): string {
  if (difference === null) return NO_VALUE;
  const points = Math.round(difference * POINTS_PER_UNIT * 10) / 10;
  if (points === 0) return '0.0 pts';
  return `${points > 0 ? '+' : '-'}${Math.abs(points).toFixed(1)} pts`;
}

/** 96.4 → "96". */
export function priceIndexLabel(index: number | null): string {
  return index === null ? NO_VALUE : `${Math.round(index)}`;
}

/** 96 → "4% below the median", 108 → "8% above the median". */
export function priceIndexHint(index: number | null): string {
  if (index === null) return 'No program with enough events to compare';
  const gap = Math.round(index - 100);
  if (gap === 0) return 'At the company median';
  return `${Math.abs(gap)}% ${gap < 0 ? 'below' : 'above'} the company median`;
}

/** The method notes the section shows under the table. */
export function mixNote(minProgramEvents: number): string {
  return `Expected margin is what a rep would earn if each of their events made its program's company average margin over the same dates. Programs with fewer than ${minProgramEvents} events use the overall company margin. "vs mix" is the gap: above zero means the rep earns more than their program mix explains.`;
}

export function priceNote(minProgramEvents: number, priceFlagBelow: number): string {
  return `Price index compares the rep's median revenue per participant with the company median for the same program, averaged over their programs by event count. 100 is the company median; 96 means 4% below. Programs with fewer than ${minProgramEvents} events with a headcount, and Custom programs, are left out. Programs ${Math.round(priceFlagBelow * 100)}% or more below the median are flagged on the rep's page.`;
}
